import { useQuery } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { useTranslate } from "ra-core";
import { Spinner } from "@/components/ui/spinner";
import { AsideSection } from "../misc/AsideSection";
import { lookupEnchargeSubscriptions } from "./encharge/api";
import { ENCHARGE_BRANDS } from "./encharge/instances";
import type { EnchargeLookupMatch } from "./encharge/types";

function getBrandLabel(
  instance: EnchargeLookupMatch["instance"],
  translate: (key: string, options?: { _: string }) => string,
): string {
  const brand = ENCHARGE_BRANDS.find((b) => b.id === instance);
  if (!brand) return instance;
  return translate(brand.labelKey, { _: brand.fallback });
}

function CategoryIcons({
  categories,
}: {
  categories: Record<string, boolean>;
}) {
  const optedIn = Object.entries(categories).filter(([, v]) => v);
  if (optedIn.length === 0) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }

  return (
    <ul className="space-y-1">
      {optedIn.map(([label]) => (
        <li key={label} className="flex items-center gap-1.5 text-xs">
          <Check className="size-3 text-primary shrink-0" />
          <span>{label}</span>
        </li>
      ))}
    </ul>
  );
}

export const EmailListSubscriptionsWidget = ({
  emails = [],
  companyName,
  enabled = true,
}: {
  emails?: string[];
  companyName?: string;
  enabled?: boolean;
}) => {
  const translate = useTranslate();
  const hasLookup = emails.length > 0 || !!companyName?.trim();

  const { data: matches = [], isPending, error } = useQuery({
    queryKey: [
      "email-lists",
      "lookup",
      emails.join(","),
      companyName?.trim() ?? "",
    ],
    queryFn: () =>
      lookupEnchargeSubscriptions({
        emails,
        companyName: companyName?.trim(),
      }),
    enabled: enabled && hasLookup && import.meta.env.VITE_IS_DEMO !== "true",
    staleTime: 10 * 60 * 1000,
  });

  const matchesWithSubscriptions = matches.filter((match) =>
    Object.values(match.communicationCategories).some(Boolean),
  );

  if (import.meta.env.VITE_IS_DEMO === "true") {
    return (
      <AsideSection
        title={translate("crm.email_lists.widget_title", {
          _: "Email lists",
        })}
      >
        <p className="text-muted-foreground text-xs">
          {translate("crm.email_lists.demo_unavailable", {
            _: "Email lists require Encharge configuration and are not available in demo mode.",
          })}
        </p>
      </AsideSection>
    );
  }

  if (!hasLookup) {
    return (
      <AsideSection
        title={translate("crm.email_lists.widget_title", {
          _: "Email lists",
        })}
      >
        <p className="text-muted-foreground text-xs">
          {translate("crm.email_lists.widget_no_email", {
            _: "Add an email address to see list subscriptions.",
          })}
        </p>
      </AsideSection>
    );
  }

  return (
    <AsideSection
      title={translate("crm.email_lists.widget_title", { _: "Email lists" })}
      noGap
    >
      {isPending ? (
        <div className="flex items-center gap-2 py-1 text-muted-foreground text-xs">
          <Spinner className="size-3" />
          <span>
            {translate("crm.email_lists.loading", { _: "Loading..." })}
          </span>
        </div>
      ) : null}

      {error ? (
        <p className="text-destructive text-xs">
          {error instanceof Error ? error.message : "Failed to load"}
        </p>
      ) : null}

      {!isPending && !error && matchesWithSubscriptions.length === 0 ? (
        <p className="text-muted-foreground text-xs">
          {translate("crm.email_lists.widget_empty", {
            _: "No email list subscriptions found.",
          })}
        </p>
      ) : null}

      {!isPending && !error && matchesWithSubscriptions.length > 0 ? (
        <ul className="space-y-3">
          {matchesWithSubscriptions.map((match) => (
            <li
              key={`${match.instance}-${match.id}`}
              className="rounded-md border px-3 py-2"
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="font-medium text-xs">
                  {getBrandLabel(match.instance, translate)}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {match.matchReason === "email"
                    ? translate("crm.email_lists.match_email", {
                        _: "Matched by email",
                      })
                    : translate("crm.email_lists.match_company", {
                        _: "Matched by company",
                      })}
                </span>
              </div>
              <p className="text-xs text-muted-foreground truncate mb-2">
                {match.email}
              </p>
              <CategoryIcons categories={match.communicationCategories} />
            </li>
          ))}
        </ul>
      ) : null}
    </AsideSection>
  );
};
